import { PeriodType } from '@thinkalpha/language-services';
import type { BarLength } from '@thinkalpha/ui-types/contracts/timeframe';
import type { OHLCVBar } from 'src/services/LightweightChartBarDataService/bars';

/** Visual mode the model is currently shaped for. Drives which derived data signal a variant consumes. */
export type ChartMode = 'line' | 'ohlc' | 'sparkline';

/**
 * Session selector for Bardex/`/tables/trading-view`. `regular` / `premarket`
 * / `postmarket` map to the regular trading-hours window; `extended` widens to
 * the full pre + core + post session.
 */
export type ChartSession = 'regular' | 'extended' | 'premarket' | 'postmarket';

export interface LightweightChartState {
    symbol: string;
    barLength: BarLength;
    session: ChartSession;
    mode: ChartMode;
    /** Sorted ascending by `time`, unique by `time`. */
    bars: readonly OHLCVBar[];
    loading: boolean;
    error: string | null;
    historyLoaded: boolean;
    /** Maximum bars retained in `bars`; oldest are trimmed when realtime appends overflow. */
    maxBars: number;
    /** Lookback in days used when computing the history fetch range. */
    historyLookbackDays: number;
}

export const DEFAULT_BAR_LENGTH: BarLength = { value: 1, period: PeriodType.day };
// Bardex's `/queries/tv` endpoint accepts `'regular'` for everyone but rejects
// `'extended'` for accounts without extended-hours data entitlement. Default
// to regular so the chart works for every user; callers can opt into extended
// via `setSession('extended')`.
export const DEFAULT_SESSION: ChartSession = 'regular';
export const DEFAULT_MODE: ChartMode = 'line';
export const DEFAULT_MAX_BARS = 1000;
export const DEFAULT_HISTORY_LOOKBACK_DAYS = 90;

export function createInitialChartState(overrides: Partial<LightweightChartState> = {}): LightweightChartState {
    return {
        symbol: '',
        barLength: DEFAULT_BAR_LENGTH,
        session: DEFAULT_SESSION,
        mode: DEFAULT_MODE,
        bars: [],
        loading: false,
        error: null,
        historyLoaded: false,
        maxBars: DEFAULT_MAX_BARS,
        historyLookbackDays: DEFAULT_HISTORY_LOOKBACK_DAYS,
        ...overrides,
    };
}
